import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useEffect, useState, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";
const API_URL = apiUrl("/nms_analysis");
const NMS = () => {
  const token = localStorage.getItem("authToken");
  const masked = useMaskNames();
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);

  // GRID MODE
  const [statusMode, setStatusMode] = useState("ALL");

  // GRAPH FILTERS
  const [selectedPharmacy, setSelectedPharmacy] = useState("ALL");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [groupMode, setGroupMode] = useState("week");

  // ---------------- FETCH ----------------
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const members = JSON.parse(localStorage.getItem("members") || "[]");
        const organisationName = localStorage.getItem("organisationName") || "";
        const allowedPharmacies = organisationName === "Pharmace all" ? ["xxx"] : members.map(m => m.pharmacyId);
        const res = await fetch(API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            allowedPharmacies
          })
        });
        const json = await res.json();
        setRawData(json.data || []);
      } catch (err) {
        console.error("Error loading NMS:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // ---------------- DERIVED ----------------
  const pharmacies = useMemo(() => {
    return rawData.filter(p => (p.nms || []).some(group => (group.breakdown || []).some(b => (b.count || 0) > 0))).map(p => p.pharmacyName).sort((a, b) => a.localeCompare(b));
  }, [rawData]);
  const statuses = useMemo(() => {
    const set = new Set();
    rawData.forEach(p => {
      (p.nms || []).forEach(group => {
        (group.breakdown || []).forEach(b => {
          if (b.statusId) set.add(b.statusId);
        });
      });
    });
    return Array.from(set).sort();
  }, [rawData]);

  // ---------------- GRID DATA ----------------
  const gridData = useMemo(() => {
    const grid = {};
    rawData.forEach(p => {
      grid[p.pharmacyName] = {};
      (p.nms || []).forEach(group => {
        (group.breakdown || []).forEach(b => {
          if (statusMode !== "ALL" && group.type !== statusMode) return;
          const status = b.statusId || "UNKNOWN";
          if (!grid[p.pharmacyName][status]) {
            grid[p.pharmacyName][status] = 0;
          }
          grid[p.pharmacyName][status] += b.count || 0;
        });
      });
    });
    return grid;
  }, [rawData, statusMode]);

  // ---------------- GRAPH DATA ----------------
  const graphData = useMemo(() => {
    const grouped = {};
    rawData.forEach(p => {
      if (selectedPharmacy !== "ALL" && p.pharmacyName !== selectedPharmacy) return;
      (p.nms || []).forEach(group => {
        (group.breakdown || []).forEach(b => {
          if (selectedStatus !== "ALL" && (b.statusId || "").toLowerCase().trim() !== selectedStatus.toLowerCase().trim()) return;
          const weeks = b.weeksSinceCreated ?? 0;
          let key = "";
          if (groupMode === "day") key = `Day ${weeks * 7}`;
          if (groupMode === "week") key = `Week ${weeks}`;
          if (groupMode === "month") key = `Month ${Math.floor(weeks / 4)}`;
          if (groupMode === "year") key = `Year ${Math.floor(weeks / 52)}`;
          if (!grouped[key]) {
            grouped[key] = {
              inProgress: 0,
              completed: 0
            };
          }
          if (group.type === "IN_PROGRESS") grouped[key].inProgress += b.count || 0;
          if (group.type === "COMPLETED") grouped[key].completed += b.count || 0;
        });
      });
    });
    return Object.entries(grouped).map(([date, vals]) => ({
      date,
      inProgress: vals.inProgress || 0,
      completed: vals.completed || 0
    }));
  }, [selectedPharmacy, selectedStatus, groupMode, rawData]);

  // ---------------- CHART SERIES ----------------
  const chartSeries = useMemo(() => {
    if (!graphData.length) return [];
    return [{
      name: "In Progress",
      type: "bar",
      stack: "total",
      data: graphData.map(d => d.inProgress),
      itemStyle: {
        color: "#FFC53D"
      }
    }, {
      name: "Completed",
      type: "bar",
      stack: "total",
      data: graphData.map(d => d.completed),
      itemStyle: {
        color: "green"
      }
    }];
  }, [graphData]);
  const xAxis = graphData.map(d => d.date);
  const chartOption = {
    tooltip: {
      trigger: "axis"
    },
    legend: {
      top: 0,
      data: chartSeries.map(s => s.name)
    },
    grid: {
      top: 80,
      left: 20,
      right: 20
    },
    xAxis: {
      type: "category",
      data: xAxis,
      axisLabel: {
        rotate: 45
      }
    },
    yAxis: {
      type: "value"
    },
    series: chartSeries
  };

  // ---------------- UI ----------------
  return <div style={{
    padding: 10
  }}>

      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }}>
        <h2>NMS Analysis</h2>
        <MaskToggleButton />
      </div>

      {/* STATUS BUTTONS */}
      <div style={{
      marginBottom: 10
    }}>
        {["ALL", "IN_PROGRESS", "COMPLETED"].map(s => <button key={s} onClick={() => setStatusMode(s)} style={{
        marginRight: 10,
        padding: "6px 12px",
        background: statusMode === s ? "#3b82f6" : "#F0F0F0",
        color: statusMode === s ? "#fff" : "#000",
        border: "none",
        borderRadius: 4,
        cursor: "pointer"
      }}>
            {s}
          </button>)}
      </div>

      {/* GRID */}
      <div style={{
      overflow: "auto",
      marginBottom: 20,
      maxHeight: 400
    }}>
        <table style={{
        borderCollapse: "collapse",
        minWidth: statuses.length * 140
      }}>
          <thead>
            <tr>
              <th style={{
              border: "1px solid #ccc",
              padding: 6,
              position: "sticky",
              left: 0,
              background: "#fff",
              zIndex: 2
            }}>
                Pharmacy
              </th>

              {statuses.map(s => <th key={s} style={{
              border: "1px solid #ccc",
              padding: 6,
              position: "sticky",
              top: 0,
              background: "#fff",
              zIndex: 1
            }}>
                  {s}
                </th>)}
            </tr>
          </thead>

          <tbody>
            {pharmacies.map(p => <tr key={p}>
                <td style={{
              border: "1px solid #ccc",
              padding: 6,
              position: "sticky",
              left: 0,
              background: "#fff",
              zIndex: 1
            }}>
                  {displayName(p, masked)}
                </td>

                {statuses.map(s => <td key={s} style={{
              border: "1px solid #ccc",
              padding: 6,
              textAlign: "center",
              background: (gridData[p]?.[s] || 0) > 0 ? statusMode === "COMPLETED" ? "#B7EB8F" : statusMode === "IN_PROGRESS" ? "#FFC53D" : "#E6F7FF" : ""
            }}>
                    {gridData[p]?.[s] || 0}
                  </td>)}
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* FILTERS */}
      <div style={{
      display: "flex",
      gap: 10,
      marginBottom: 10,
      width: "100%"
    }}>
        <select value={selectedPharmacy} onChange={e => setSelectedPharmacy(e.target.value)}>
          <option value="ALL">All pharmacies</option>
          {pharmacies.map(p => <option key={p} value={p}>{displayName(p, masked)}</option>)}
        </select>

        <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
          <option value="ALL">All statuses</option>
          {statuses.map(s => <option key={s}>{s}</option>)}
        </select>

        <select value={groupMode} onChange={e => setGroupMode(e.target.value)}>
          <option value="day">By day</option>
          <option value="week">By week</option>
          <option value="month">By month</option>
          <option value="year">By year</option>
        </select>
      </div>

      {/* GRAPH */}
      <div style={{
      marginTop: 40,
      border: "1px solid #ddd",
      padding: 10,
      background: "#fff"
    }}>
        <div style={{
        overflowX: "auto",
        overflowY: "hidden"
      }}>
          <div style={{
          width: Math.max(800, xAxis.length * 80),
          height: 400
        }}>
            <ReactECharts key={`${selectedPharmacy}-${selectedStatus}-${groupMode}`} option={chartOption} style={{
            height: "100%",
            width: "100%"
          }} />
          </div>
        </div>
      </div>

      {loading && <div style={{
      marginTop: 10
    }}>Loading...</div>}

    </div>;
};
export default NMS;
